import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Direction_Enum,
  Field_Enum,
  Operator_Enum,
} from '@weave/schema-gen-ts/dist/schemas/appointments/v3/service.pb';
import { DestinationType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import {
  DynamicFieldProperty_Enum,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { PropertyBinding } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/service.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { isEqual } from 'lodash-es';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { useListBrandSettings } from '@frontend/api-brand-settings';
import { EmailRecordsV1 } from '@frontend/api-email-records';
import { PersonsV3 } from '@frontend/api-person';
import { PetsV1 } from '@frontend/api-pets';
import { SubscriptionsQueries } from '@frontend/api-subscription';
import { TemplatorV2Hooks, TemplatorV2Queries } from '@frontend/api-templator-v2';
import {
  getPetBindingFromAppointmentData,
  LinkData,
  PropertyBindingsData,
  TemplateFlowPopoverItem,
  UseTemplateFlowResult,
} from '@frontend/integrated-messaging';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { usePracticeName } from '@frontend/practice-name';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { StateRefHelpers } from '@frontend/state-ref-helpers';
import { ModalControlResponse, useModalControl } from '@frontend/design-system';
import { RenderTemplateArgs } from '../types';
import { buildAddressFromBrandSettings, buildAddressFromSalesforce, buildMapsUrl } from '../utils';
import { ModalWrapperProps } from './use-forms-promotion-flow';
import { useIntegratedTemplateFlows } from './use-integrated-template-flows';

type UseInboxTemplateFlowsArgs = {
  groupId: string;
  contextPersonId?: string;
  personPhone: string;
  locationPhone: string;
  onRenderTemplate: (args: { bodyValue: string; linkData?: LinkData[] }) => void;
  petId?: string;
};

export type UseInboxTemplateFlowsResults = {
  availableTemplateFlows: TemplateFlowPopoverItem[];
  person?: Person;
  isPrimaryContact: boolean;
  canChangeContextContact: boolean;
  modalControl: ModalControlResponse;
  templateTypeProps: Record<string, UseTemplateFlowResult>;
  formPromotionModalProps: ModalWrapperProps;
};

export const useInboxTemplateFlows = ({
  groupId,
  contextPersonId,
  personPhone,
  locationPhone,
  onRenderTemplate,
  petId,
}: UseInboxTemplateFlowsArgs): UseInboxTemplateFlowsResults => {
  const { getLocationName, selectedOrgId, accessibleLocationData, locationData } = useAppScopeStore();
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { featureFlags, getFeatureFlagValue } = useAppFlagStore();
  const isVet = lastUsedVertical === Vertical.VET;
  const petNameEnabled = !!featureFlags[groupId]?.get('nwx:manual-templates-pet-name')?.value;

  const isBrandSettingsEnabled = getFeatureFlagValue({ locationIds: [groupId], flagName: 'brand-settings' });

  const { data: primaryContactId } = PersonsV3.PersonQueries.useGetPrimaryContactLegacyQuery(
    {
      phoneNumber: personPhone,
      locationId: groupId,
    },
    { enabled: !contextPersonId && !!personPhone && !!groupId, select: (data) => data.personId }
  );
  const { data: preferredPersons = [] } = PersonsV3.PersonQueries.useListPreferredPersonsByPhoneQuery(
    {
      phoneNumber: personPhone,
      locationId: groupId,
    },
    { enabled: !!personPhone && !!groupId, select: (data) => data.persons }
  );
  // Fall back to first preferred person if primary contact is not found (similar to useAssociatedContacts behavior)
  const fallbackPersonId = preferredPersons[0]?.personId;
  const personId = contextPersonId || primaryContactId || fallbackPersonId;
  const preferredPersonsCount = preferredPersons.length;
  const canChangeContextContact = preferredPersonsCount > 1;

  const { data: person } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: personId ?? '',
      locationIds: [groupId],
    },
    {
      enabled: !!personId && !!groupId,
    }
  );
  const petsQuery = PetsV1.Queries.useFetchPetsByPersonIdQuery(
    {
      personId: personId ?? '',
      locationId: groupId,
    },
    {
      enabled: !!personId && isVet && petNameEnabled && !!groupId,
    }
  );
  const { timestamp } = StateRefHelpers.useStableRoundedTimestamp({ roundingDirection: 'up' });
  const { data: latestPastAppointment } = AppointmentsApiQueries.useListAppointments(
    {
      filters: [
        {
          field: Field_Enum.PERSON_ID,
          operator: Operator_Enum.EQUAL,
          value: personId ?? '',
        },
        {
          field: Field_Enum.APPOINTMENT_AT,
          operator: Operator_Enum.LT,
          value: timestamp.toISOString(),
        },
        !!petId && { field: Field_Enum.SERVICEABLE_ID, operator: Operator_Enum.EQUAL, value: petId },
      ].filter((item) => !!item),
      sorts: [
        {
          field: Field_Enum.APPOINTMENT_AT,
          direction: Direction_Enum.DESC,
        },
      ],
      locationId: groupId,
      limit: 1,
    },
    {
      enabled: !!personId && !!isVet && !!timestamp && !!groupId,
    }
  );

  const modalControl = useModalControl();
  const slug = locationData[groupId]?.slug ?? '';

  const { data: salesforceAccount } = SubscriptionsQueries.useGetSalesforceAccountBySlug({
    slug,
    enabled: !!slug,
  });

  const { data: locationDomainRecord } = EmailRecordsV1.Queries.useGetLocationPersonalizationQuery({
    locationId: groupId,
  });

  const { data: brandSettings = {} } = useListBrandSettings({
    locationIds: [groupId],
    orgId: selectedOrgId,
    queryOptions: {
      enabled: isBrandSettingsEnabled,
    },
  });

  const defaultPropertyBindingsData = TemplatorV2Hooks.useDefaultPropertyBindingsData({
    businessGroupIds: [groupId],
    orgId: selectedOrgId,
  });
  const timezone = accessibleLocationData[groupId]?.timezone ?? '';
  const [renderData, setRenderData] = useState<{
    payload: Parameters<(typeof TemplatorV2Queries)['useRender']>[0];
    linkData?: LinkData[];
  }>();
  const renderedTemplateQuery = TemplatorV2Queries.useRender(
    renderData?.payload ?? {
      templateTypeSlug: TemplateType_Slug.UNSPECIFIED_TEMPLATE_TYPE,
      templateString: '',
      bindingsList: [],
      timezone,
      // TODO: get the locale dynamically once we have a way to do so
      locale: 'en_US',
      destinationType: DestinationType_Enum.SMS,
      keepEmptyDynamicFields: false,
    },
    {
      enabled: !!renderData && !!groupId,
      onSuccess: (data) => {
        onRenderTemplate({
          bodyValue: data.message.message,
          linkData: renderData?.linkData,
        });
      },
    }
  );

  const practiceName = usePracticeName(groupId);
  const getBindingsList = useCallback(
    (additionalBindings: PropertyBindingsData = {}): PropertyBinding[] => {
      const emailAddress = locationDomainRecord?.locationPersonalization?.replyTo?.email;
      const brandSetting = brandSettings[groupId];

      const contextPropertyBindings: PropertyBindingsData = {
        [DynamicFieldProperty_Enum.ORG_NAME]: getLocationName(selectedOrgId),
        [DynamicFieldProperty_Enum.BUSINESS_GROUP_NAME]: practiceName,
        [DynamicFieldProperty_Enum.BUSINESS_GROUP_PHONE]: locationPhone,
      };

      if (person) {
        contextPropertyBindings[DynamicFieldProperty_Enum.FIRST_NAME] = person.firstName;
        contextPropertyBindings[DynamicFieldProperty_Enum.LAST_NAME] = person.lastName;
        contextPropertyBindings[DynamicFieldProperty_Enum.PREFERRED_NAME] = person.preferredName || person.firstName;
      }

      // [Brand Settings ON → email from brand settings] [Brand Settings OFF → email from email settings]
      const practiceEmail = isBrandSettingsEnabled ? brandSetting?.replyToEmail || emailAddress : emailAddress;
      if (practiceEmail) {
        contextPropertyBindings[DynamicFieldProperty_Enum.BUSINESS_GROUP_EMAIL] = practiceEmail;
      }

      // [Brand Settings ON → phone from brand settings] [Brand Settings OFF → phone from location data]
      const practicePhoneNumber = isBrandSettingsEnabled
        ? brandSetting?.locationPhoneNumbers?.find((phone) => phone.id === brandSetting?.selectedPhoneNumberId)?.number
        : locationPhone;
      if (practicePhoneNumber) {
        contextPropertyBindings[DynamicFieldProperty_Enum.BUSINESS_GROUP_PHONE_LINK] = `tel:${practicePhoneNumber}`;
      }

      // [Brand Settings ON → address from brand settings] [Brand Settings OFF → address from Salesforce]
      const businessGroupAddress = isBrandSettingsEnabled
        ? buildAddressFromBrandSettings(brandSetting?.locationAddress)
        : buildAddressFromSalesforce(salesforceAccount?.account);

      if (businessGroupAddress) {
        contextPropertyBindings[DynamicFieldProperty_Enum.BUSINESS_GROUP_ADDRESS] = businessGroupAddress;

        contextPropertyBindings[DynamicFieldProperty_Enum.BUSINESS_GROUP_ADDRESS_LINK] =
          buildMapsUrl(businessGroupAddress);
      }

      return Object.entries({
        ...defaultPropertyBindingsData,
        ...contextPropertyBindings,
        ...additionalBindings,
      }).reduce<PropertyBinding[]>((acc, [property, value]) => {
        const typedProperty = Object.keys(DynamicFieldProperty_Enum).includes(property)
          ? (property as DynamicFieldProperty_Enum)
          : undefined;
        if (typedProperty && value)
          acc.push({
            property: typedProperty,
            values: [value],
          });
        return acc;
      }, []);
    },
    [
      groupId,
      person,
      locationPhone,
      selectedOrgId,
      getLocationName,
      defaultPropertyBindingsData,
      !!petsQuery.data,
      brandSettings,
      isBrandSettingsEnabled,
      locationDomainRecord,
      salesforceAccount,
      practiceName,
    ]
  );

  const renderTemplate = ({ template, linkData, additionalBindings }: RenderTemplateArgs) => {
    const appointmentId = linkData?.find(
      (link) => link.relatedType === RelatedType.RELATED_TYPE_APPOINTMENT
    )?.relatedId;
    const latestAppointment = latestPastAppointment?.appointments?.length
      ? latestPastAppointment?.appointments?.[0]
      : undefined;
    const bindingsList = getBindingsList({
      [DynamicFieldProperty_Enum.PET_NAME]:
        getPetBindingFromAppointmentData(
          {
            allAppointments: latestAppointment ? [latestAppointment] : [],
            futureAppointmentCount: 0,
            latestAppointment,
          },
          petsQuery.data?.pets ?? [],
          appointmentId
        ) || '[Pet Name]',
      ...additionalBindings,
    });

    setRenderData(() => {
      const newPayload: Parameters<(typeof TemplatorV2Queries)['useRender']>[0] = {
        templateString: template.templateString,
        templateTypeSlug: template.templateTypeSlug,
        bindingsList,
        timezone,
        // TODO: get the locale dynamically once we have a way to do so
        locale: 'en_US',
        destinationType: DestinationType_Enum.SMS,
        keepEmptyDynamicFields: true,
      };

      if (renderedTemplateQuery.data && isEqual(newPayload, renderData?.payload)) {
        onRenderTemplate({
          bodyValue: renderedTemplateQuery.data.message.message,
          linkData: renderData?.linkData,
        });
      }

      return {
        payload: newPayload,
        linkData,
      };
    });
  };

  useEffect(() => {
    if (renderedTemplateQuery.data) {
      onRenderTemplate({
        bodyValue: renderedTemplateQuery.data.message.message,
        linkData: renderData?.linkData,
      });
    }
  }, [renderedTemplateQuery.data]);

  const { popoverItems, templateTypeProps, formPromotionModalProps } = useIntegratedTemplateFlows({
    groupId,
    personId,
    personPhone,
    renderTemplate,
    modalControl,
  });

  const templateFlowState = useMemo(
    () => ({
      availableTemplateFlows: popoverItems,
      person,
      isPrimaryContact: !!personId && personId === primaryContactId,
      canChangeContextContact,
      modalControl,
      templateTypeProps,
      formPromotionModalProps,
    }),
    [
      popoverItems,
      templateTypeProps,
      person,
      personId,
      primaryContactId,
      canChangeContextContact,
      modalControl,
      formPromotionModalProps,
    ]
  );

  return templateFlowState;
};
